import { FilingFormState } from '../filing-form-state.service';
import { SheetProjector } from '../sheet-projector';

/** Minimal control shape the director-count projector needs. */
interface DirectorCountControl {
  qname: string;
  ui?: { directorNumber?: number };
}

/**
 * Build a pure {@link SheetProjector} for the director-triplet sheets (roles 4
 * and 5), whose conditional visibility is `ui.directorNumber <= visibleCount`
 * rather than a declarative `conditional` block. The projector strips any field
 * whose schema `ui.directorNumber` exceeds the current visible-director count,
 * leaving fields without a `directorNumber` (e.g. role 5's "other person"
 * fields) untouched.
 *
 * `getVisibleCount` is the sheet's OWN count resolver (e.g. getVisibleDirectorCount)
 * so the projector and the mounted component's shouldShowField compute the count
 * identically — no parity drift. ALWAYS pass the sheet's shared resolver, never a
 * hand-rolled parser: parity-by-construction is the whole point of this seam, and
 * a second parser (different empty/`0`/`NaN` handling) would silently reintroduce
 * the drift it exists to prevent. Pure: never mutates `sheetData`.
 */
export function makeDirectorCountProjector(
  sheetKey: keyof FilingFormState,
  getVisibleCount: (sheetData: Record<string, any>) => number,
  controls: DirectorCountControl[]
): SheetProjector {
  const directorFieldNumbers = new Map<string, number>();
  for (const control of controls) {
    if (typeof control.ui?.directorNumber === 'number') {
      directorFieldNumbers.set(control.qname, control.ui.directorNumber);
    }
  }
  return {
    sheetKey,
    project(sheetData: Record<string, any>): Record<string, any> {
      const visibleCount = getVisibleCount(sheetData);
      const projected: Record<string, any> = { ...sheetData };
      for (const [qname, directorNumber] of directorFieldNumbers) {
        if (directorNumber > visibleCount && qname in projected) {
          delete projected[qname];
        }
      }
      return projected;
    },
  };
}
